import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import api from 'state/axios';
import { formatDate } from 'state/helpers';
import CustomDatePicker from 'components/CustomDatePicker';
import FlexBetween from 'components/FlexBetween';
import SectionLabel from 'components/SectionLabel';
import FieldDisplay from 'components/FieldDisplay';


const CONTRACT_TYPES = [
  'Fixed-Term', 
  'Indefinite-Term', 
  'Probation', 
];

const Employment = ({ 
  values, 
  handleChange, 
  handleBlur, 
  errors, 
  touched, 
  setFieldValue, 
  isEditMode 
}) => {
  const crewRanks = useSelector((state) => state.crewRanks);
  const [airports, setAirports] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [deptUnits, setDeptUnits] = useState([]);
  const [jobGrades, setJobGrades] = useState([]);
  const [defJobTitles, setDefJobTitles] = useState([]);

  useEffect(() => {
    const fetchRefData = async () => {
      try {
        const [airRes, divRes, deptRes, duRes, jgRes, jtRes] = await Promise.all([
          api.get('/airports/airportswithtravelzone'),
          api.get('/divisions/active'),
          api.get('/departments/active'),
          api.get('/deptunits/active'),
          api.get('/jobgrades/active'),
          api.get('/jobtitles/active'),
        ]);
        setAirports(airRes.data || []);
        setDivisions(divRes.data || []);
        setDepartments(deptRes.data || []);
        setDeptUnits(duRes.data || []);
        setJobGrades(jgRes.data || []);
        setDefJobTitles(jtRes.data || []);
      } catch { /* silently fail - fields remain usable */ }
    };
    fetchRefData();
  }, []);

  const sortedRanks = crewRanks
    ?.slice()
    .sort((a, b) => (a.rankPriority ?? Infinity) - (b.rankPriority ?? Infinity)) || [];

  if (!isEditMode) return (
    <Box 
      display="grid" 
      gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
      gap={2}
    >
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Employee Type" value={values.employeeType} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Working Mode" value={values.workingMode} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Rank" value={values.rank} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Home Base" value={values.homeBase} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Division" value={values.division} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Department" value={values.department} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Dept Unit" value={values.deptUnit} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Agency" value={values.agency} />
      </Box>

      <Box 
        display="flex" 
        flexDirection="column" 
        gap={1} 
        sx={{ gridColumn: 'span 12' }}
      >
        <SectionLabel>Contract</SectionLabel>
        <Box 
          display="grid" 
          gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
          gap={2} 
        >
          <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
            <FieldDisplay label="Contract Type" value={values.employmentContract} />
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 4' } }}>
            <FieldDisplay label="Start Date" value={formatDate(values.employmentStartDate)} />
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 4' } }}>
            <FieldDisplay label="End Date" value={formatDate(values.employmentEndDate)} />
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
            <FieldDisplay label="Job Grade" value={values.jobGradeCode} />
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
            <FieldDisplay label="Salary Grade" value={values.salaryGradeCode} />
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
            <FieldDisplay label="Max Hours / Day" value={values.limits?.maxHoursPerDay} />
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
            <FieldDisplay label="Max Hours / Week" value={values.limits?.maxHoursPerWeek} />
          </Box>
        </Box>
      </Box>


      <Box 
        display="flex" 
        flexDirection="column" 
        gap={1} 
        sx={{ gridColumn: 'span 12' }}
      >
        <SectionLabel>Job Titles</SectionLabel>
        {!values.jobTitles?.length ? (
          <Typography variant="body2" color="text.secondary">
            No job titles recorded.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>JobTitle</TableCell>
                  <TableCell>EffectiveFrom</TableCell>
                  <TableCell>EffectiveTo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {values.jobTitles.map((jt, i) => (
                  <TableRow key={i}>
                    <TableCell>{jt.jobTitle || '-'}</TableCell>
                    <TableCell>{formatDate(jt.effectiveFrom)}</TableCell>
                    <TableCell>{jt.effectiveTo ? formatDate(jt.effectiveTo) : 'Present'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Box>
  );

  
  return (
    <Box 
      display="grid" 
      gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
      gap={2}
    >
      <TextField
        fullWidth 
        select 
        size="small" 
        label="Employee Type" 
        name="employeeType"
        value={values.employeeType} 
        onChange={handleChange}
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      >
        <MenuItem value="CREW">CREW</MenuItem>
        <MenuItem value="NON-CREW">NON-CREW</MenuItem>
      </TextField>

      <TextField
        fullWidth 
        select 
        size="small" 
        label="Working Mode" 
        name="workingMode"
        value={values.workingMode} 
        onChange={handleChange}
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        error={touched.workingMode && Boolean(errors.workingMode)}
        helperText={touched.workingMode && errors.workingMode}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      >
        <MenuItem value="OFFICE">OFFICE</MenuItem>
        <MenuItem value="SHIFT">SHIFT</MenuItem>
      </TextField>

      <TextField
        fullWidth 
        select 
        size="small" 
        label="Rank" 
        name="rank"
        value={values.rank || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      >
        <MenuItem value=""><em>None</em></MenuItem>
        {sortedRanks.map((r) => (
          <MenuItem key={r._id} value={r.rankCode}>
            {r.rankCode} - {r.description}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth 
        select 
        size="small" 
        label="Home Base" 
        name="homeBase"
        value={values.homeBase || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      >
        <MenuItem value=""><em>None</em></MenuItem>
        {airports.map((a) => (
          <MenuItem key={a.iataCode} value={a.iataCode}>
            {a.iataCode} - {a.airportName}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth
        select
        size="small"
        label="Division"
        name="division"
        value={values.division || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      >
        <MenuItem value=""><em>None</em></MenuItem>
        {divisions.map((d) => (
          <MenuItem key={d.divisionCode} value={d.divisionCode}>
            {d.divisionCode} - {d.description}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth 
        select 
        size="small" 
        label="Department" 
        name="department"
        value={values.department || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      >
        <MenuItem value=""><em>None</em></MenuItem>
        {departments.map((d) => (
          <MenuItem key={d.departmentCode} value={d.departmentCode}>
            {d.departmentCode} - {d.description}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth
        select
        size="small"
        label="Dept Unit"
        name="deptUnit"
        value={values.deptUnit || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      >
        <MenuItem value=""><em>None</em></MenuItem>
        {deptUnits
          .filter((d) => !values.department || d.departmentCode === values.department)
          .map((d) => (
            <MenuItem key={d.deptUnitCode} value={d.deptUnitCode}>
              {d.deptUnitCode} - {d.description}
            </MenuItem>
          ))}
      </TextField>
      
      <TextField
        fullWidth 
        size="small" 
        label="Agency" 
        name="agency"
        value={values.agency} 
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />

      <SectionLabel>Contract</SectionLabel>
      <TextField
        fullWidth 
        select 
        size="small" 
        label="Contract Type" 
        name="employmentContract"
        value={values.employmentContract || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      >
        <MenuItem value=""><em>None</em></MenuItem>
        {CONTRACT_TYPES.map((t) => (
          <MenuItem key={t} value={t}>{t}</MenuItem>
        ))}
      </TextField>

      <CustomDatePicker
        label="Start Date"
        name="employmentStartDate"
        value={values.employmentStartDate ? new Date(values.employmentStartDate) : null}
        onChange={setFieldValue}
        touched={touched}
        errors={errors}
        size="small"
        variant="outlined"
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />
      <CustomDatePicker
        label="End Date"
        name="employmentEndDate"
        value={values.employmentEndDate ? new Date(values.employmentEndDate) : null}
        onChange={setFieldValue}
        touched={touched}
        errors={errors}
        size="small"
        variant="outlined"
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
      />

      <TextField
        fullWidth 
        select 
        size="small" 
        label="Job Grade"
        value={values.jobGradeCode || ''}
        onChange={(e) => setFieldValue('jobGradeCode', e.target.value)}
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      >
        <MenuItem value=""><em>None</em></MenuItem>
        {jobGrades.map((j) => (
          <MenuItem key={j.jobGradeCode} value={j.jobGradeCode}>
            {j.jobGradeCode}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth 
        size="small" 
        label="Salary Grade" 
        name="salaryGradeCode"
        value={values.salaryGradeCode} 
        onChange={handleChange}
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />

      <TextField
        fullWidth 
        size="small" 
        label="Max Hours / Day" 
        type="number"
        value={values.limits?.maxHoursPerDay ?? ''}
        onChange={(e) => setFieldValue('limits.maxHoursPerDay', Number(e.target.value))}
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />

      <TextField
        fullWidth 
        size="small" 
        label="Max Hours / Week" 
        type="number"
        value={values.limits?.maxHoursPerWeek ?? ''}
        onChange={(e) => setFieldValue('limits.maxHoursPerWeek', Number(e.target.value))}
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />

      <Box 
        display="flex" 
        flexDirection="column" 
        gap={1} 
        sx={{ gridColumn: 'span 12' }}
      >
        <FlexBetween>
          <SectionLabel>Job Titles</SectionLabel>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => setFieldValue('jobTitles', [
              ...values.jobTitles,
              { jobTitle: '', effectiveFrom: new Date(), effectiveTo: null },
            ])}
          >
            Add Job Title
          </Button>
        </FlexBetween>

        {values.jobTitles.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No job titles recorded.
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {values.jobTitles.map((jt, i) => {
              const update = (field, val) => {
                const next = [...values.jobTitles];
                next[i] = { ...next[i], [field]: val };
                setFieldValue('jobTitles', next);
              };
              return (
                <Box
                  key={i}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
                >
                  <Box 
                    flex={1}
                    display="grid" 
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
                    gap={2}
                  >
                    <TextField
                      fullWidth
                      select
                      size="small"
                      label="Job Title"
                      value={jt.jobTitle || ''}
                      onChange={(e) => update('jobTitle', e.target.value)}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {defJobTitles.map((j) => (
                        <MenuItem key={j.jobTitleCode} value={j.jobTitleCode}>
                          {j.jobTitleCode} - {j.description}
                        </MenuItem>
                      ))}
                    </TextField>
                    <CustomDatePicker
                      label="Effective From"
                      name={`jobTitles[${i}].effectiveFrom`}
                      value={jt.effectiveFrom ? new Date(jt.effectiveFrom) : null}
                      onChange={(_, val) => update('effectiveFrom', val)}
                      size="small"
                      variant="outlined"
                      sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                    />
                    <CustomDatePicker
                      label="Effective To"
                      name={`jobTitles[${i}].effectiveTo`}
                      value={jt.effectiveTo ? new Date(jt.effectiveTo) : null}
                      onChange={(_, val) => update('effectiveTo', val)}
                      size="small"
                      variant="outlined"
                      sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                    />
                  </Box>
                  <Box display="flex" justifyContent="flex-end">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setFieldValue('jobTitles', values.jobTitles.filter((_, idx) => idx !== i))}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Employment;
